import type { Anime, CellResult, GuessResult } from './types';
import { isAnimeBanned } from './killswitch';
import data from '../data/anime.json';

export const POOL = (data as { anime: Anime[] }).anime;

// Pool with kill-switch/ecchi-banned titles removed (best-practice §4.4). Used for
// the daily answer and search; lookups by id still use the full POOL.
export function activePool(): Anime[] {
  const filtered = POOL.filter((a) => !isAnimeBanned(a.id));
  return filtered.length ? filtered : POOL;
}

// ---- daily deterministic answer (UTC) — best-practice §2.1 ----

export function todayKey(d = new Date()): string {
  return d.toISOString().slice(0, 10); // 'YYYY-MM-DD' in UTC
}

// FNV-1a 32-bit — stable across machines/runtimes
export function fnv1a(str: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

// Puzzle number = days since launch. Launch day is #1 (best-practice §11.4 期号).
const LAUNCH = Date.UTC(2026, 6, 1); // 2026-07-01 planned launch — adjust to real launch date
export function dayNumber(d = new Date()): number {
  const today = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  return Math.max(1, Math.floor((today - LAUNCH) / 86400000) + 1);
}

export function dailyAnime(mode = 'classic', d = new Date()): Anime {
  const pool = activePool();
  return pool[fnv1a(`${todayKey(d)}:${mode}`) % pool.length];
}

// Free opening clue (best-practice §2.3): decade + a genre of the answer. Non-counting.
export function dailyHint(answer: Anime): { decade: string; genre: string } {
  const decade = answer.year ? `${Math.floor(answer.year / 10) * 10}` : '20??';
  const genre = answer.genres[0] ?? 'anime';
  return { decade, genre };
}

// ms until next UTC midnight — for the countdown (best-practice §11.4)
export function msUntilNextUTCDay(d = new Date()): number {
  const next = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1);
  return next - d.getTime();
}

// ---- guess comparison ----

function numeric(guess: number | null, answer: number | null, close: number): CellResult {
  if (guess == null || answer == null) return { state: 'wrong', direction: null };
  if (guess === answer) return { state: 'correct', direction: null };
  const direction = answer > guess ? 'up' : 'down';
  const state = Math.abs(answer - guess) <= close ? 'partial' : 'wrong';
  return { state, direction };
}

function exact(guess: string | null, answer: string | null): CellResult {
  const state = guess != null && guess === answer ? 'correct' : 'wrong';
  return { state, direction: null };
}

function setOverlap(guess: string[], answer: string[]): CellResult {
  const a = new Set(answer);
  const hits = guess.filter((g) => a.has(g));
  if (hits.length === answer.length && guess.length === answer.length)
    return { state: 'correct', direction: null };
  if (hits.length > 0) return { state: 'partial', direction: null };
  return { state: 'wrong', direction: null };
}

export function compare(guess: Anime, answer: Anime): GuessResult {
  return {
    anime: guess,
    isAnswer: guess.id === answer.id,
    year: numeric(guess.year, answer.year, 2),
    season: exact(guess.season, answer.season),
    episodes: numeric(guess.episodes, answer.episodes, 4),
    studio: exact(guess.studio, answer.studio),
    genres: setOverlap(guess.genres, answer.genres),
    score: numeric(guess.score, answer.score, 5),
  };
}

// ---- search / display ----

export function displayTitle(a: Anime): string {
  return a.title.english || a.title.romaji || a.title.native || '???';
}

function allTitleStrings(a: Anime): string[] {
  return [a.title.english, a.title.romaji, a.title.native, ...a.synonyms].filter(
    (s): s is string => !!s,
  );
}

export function searchPool(query: string, exclude: Set<number>, limit = 8): Anime[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const out: Anime[] = [];
  for (const a of activePool()) {
    if (exclude.has(a.id)) continue;
    if (allTitleStrings(a).some((t) => t.toLowerCase().includes(q))) {
      out.push(a);
      if (out.length >= limit) break;
    }
  }
  return out;
}
